import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import EnvBanner from './components/EnvBanner';
import ProtectedRoute from './components/ProtectedRoute';
import GuestRoute from './components/GuestRoute';
import LaunchGate from './components/LaunchGate';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import ProductPage from './pages/ProductPage';
import GuidePage from './pages/GuidePage';
import PricingPage from './pages/PricingPage';
import ComparisonPage from './pages/ComparisonPage';
import FaqPage from './pages/FaqPage';
import ContactPage from './pages/ContactPage';
import FeedbackPage from './pages/FeedbackPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CookiePage from './pages/CookiePage';
import RemovalPage from './pages/RemovalPage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifierPage from './pages/VerifierPage';
import SellerProfilePage from './pages/SellerProfilePage';
import AccountPage from './pages/AccountPage';
import AdminMonitorMetricsPage from './pages/AdminMonitorMetricsPage';
import WorkingIndicator from './components/WorkingIndicator'; // TEMP: preview only
// CompanyEnrichPage is now embedded inside VerifierPage (same window, mode toggle)
import './styles/global.css';

// The demo route runs the real app on fabricated data, with no environment badge, for
// recording Help Center clips. That is exactly what makes it unsafe to ship: a page that
// looks like RepBuddy, is full of invented contacts and invented usage figures, and carries
// nothing to say so. Local and dev builds only.
//
// Gated on NODE_ENV, not on REACT_APP_APP_ENV, and that choice is deliberate. CRA feeds
// DefinePlugin a single 'process.env' object literal, so `process.env.REACT_APP_ANYTHING`
// is a member lookup that webpack cannot fold, and a branch it cannot fold is a branch whose
// import() it still bundles. NODE_ENV is defined separately as a bare string and does fold,
// which is what makes the demo chunk genuinely absent from a production build rather than
// merely unreachable within it.
//
// Practically: the demo runs under `npm start`, which is where recording happens anyway.
// Every `npm run build` output, for every environment, is free of it.
const DEMO_ENABLED = process.env.NODE_ENV !== 'production';

const DemoPage = lazy(() => {
  if (process.env.NODE_ENV !== 'production') {
    return import('./pages/DemoPage');
  }
  return Promise.resolve({ default: () => null });
});

export default function App() {
  return (
    <>
    {/* Renders nothing on production - see EnvBanner. Sits OUTSIDE ErrorBoundary (which
        swaps its children for a recovery screen) and outside the router, so the
        environment stays identifiable across every navigation and through a crash. */}
    <EnvBanner />
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public marketing site */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/compare" element={<ComparisonPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />

          {/* Dev-only preview: view the WorkingIndicator without login. Never registered in
              production builds, so it isn't publicly reachable on the live site. */}
          {process.env.NODE_ENV !== 'production' && (
            <Route
              path="/preview/working"
              element={
                <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                  <div style={{ width: '100%', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 28 }}>
                    {/* One per copy bucket, back-dated so all four are visible at once - the
                        last of them only appears after two and a half minutes of a real search,
                        which is not a practical way to review the wording. */}
                    {[0, 25_000, 75_000, 165_000].map(age => (
                      <WorkingIndicator key={age} since={Date.now() - age} />
                    ))}
                    {/* Mock input row so it sits above a composer, as it does in the app */}
                    <div style={{ marginTop: 4, display: 'flex', gap: 8, border: '1px solid var(--border-bright)', borderRadius: 12, padding: 10, background: 'var(--bg-elevated)' }}>
                      <div style={{ flex: 1, color: 'var(--text-muted)', fontSize: 14, fontFamily: 'var(--font-display)' }}>What contacts do you need?…</div>
                    </div>
                  </div>
                </div>
              }
            />
          )}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiePage />} />
          <Route path="/request-removal" element={<RemovalPage />} />
          {/* Guest-only: signed-in users are redirected to /app so they can't register
              a second account or re-open the sign-up / login forms. */}
          <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
          {/* Founding registration is CLOSED (SCRUM-46). It was unlinked but publicly
              reachable by typing the URL, and the backend granted founding status from a
              request field, so it was effectively self-service. Founding pricing is now a
              price-book assignment an admin makes (backend/scripts/price-book.js) after
              someone contacts the founders directly. Redirect rather than 404 so an old
              shared link lands somewhere useful instead of looking broken. */}
          <Route path="/founding" element={<Navigate to="/register" replace />} />
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          {/* Asking for a link is guest-only like the rest of the sign-in flow. */}
          <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
          {/* Spending a link is NOT guest-only, and that is deliberate. GuestRoute would
              bounce a signed-in user to /app, so someone still logged in on their laptop
              could never use the link they just asked for from their phone. The token is
              the authority here, not the session - and the reset revokes that session
              anyway. */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Email verification (SCRUM-49) is behind NEITHER guard, deliberately.
              GuestRoute would bounce the signed-in-but-unverified user - the one case
              ProtectedRoute sends here - straight back to /app, and the two would ping-pong.
              ProtectedRoute would lock out the far more common visitor, who has just
              registered and has no session at all yet. The page itself redirects anyone
              already verified to /app, which is the only case that does not belong here. */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Product (protected) - moved off "/" so the homepage can own the root */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <LaunchGate>
                  <VerifierPage />
                </LaunchGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <LaunchGate>
                  <SellerProfilePage />
                </LaunchGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <LaunchGate>
                  <AccountPage />
                </LaunchGate>
              </ProtectedRoute>
            }
          />
          {/* Internal dev dashboard. Login-gated here; the page's data endpoint is the real
              gate (DEV_EMAILS only), and the page shows an access-denied notice on 403. No
              LaunchGate — it isn't the product, so it must not show the launch countdown. */}
          <Route
            path="/admin_monitor_metrics"
            element={
              <ProtectedRoute>
                <AdminMonitorMetricsPage />
              </ProtectedRoute>
            }
          />
          {DEMO_ENABLED && (
            <Route
              path="/demo"
              element={
                <Suspense fallback={null}>
                  <DemoPage />
                </Suspense>
              }
            >
              <Route index element={<VerifierPage />} />
              <Route path="profile" element={<SellerProfilePage />} />
              <Route path="account" element={<AccountPage />} />
            </Route>
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
    </>
  );
}
